import { SelectOption } from '~/components/field/common/select';
import { CustomisationEditorFormField } from '~/forms/customisation-editor/customisation-editor-form-field';

export type FieldPropertyType =
	| 'String'
	| 'Number'
	| 'Select'
	| 'FieldModel'
	| 'FieldType';

type FieldsetPropertyName = 'title';

type ColumnPropertyName = 'data-colspan';

export interface PropertyMeta {
	label: string;
	type: FieldPropertyType;
	// "Select"-only
	items?: SelectOption[];
}

export interface FieldPropertyMeta extends PropertyMeta {
	name:
		| FieldsetPropertyName
		| keyof CustomisationEditorFormField
		| ColumnPropertyName;
}

interface FieldMeta {
	name: string;
	/**
	 * requirePath must be the absolute path to a component and use
	 * "PLATFORM.moduleName" or else the field may not be included in the production
	 * build (ie. referenced only in a customisation file and not within the project)
	 *
	 * example value: PLATFORM.moduleName('components/field/common/text')
	 */
	requirePath: string;
	props: readonly FieldPropertyMeta[];
	isSelectable: boolean;
}

/**
 * registerComponent will add support for editing a component on
 * the customisation editor.
 */
export function registerComponent(tagName: string, meta: FieldMeta): void {
	const existingMeta = propertiesMeta[tagName];
	if (existingMeta !== undefined) {
		throw new Error('Cannot call registerComponent twice for: ' + tagName);
	}
	propertiesMeta[tagName] = meta;
	if (meta.isSelectable) {
		componentTypes.push({
			displayName: meta.name ? meta.name : '',
			value: tagName,
		});
		componentTypes.sort((a, b) => {
			return a.displayName.localeCompare(b.displayName);
		});
	}
}

/**
 * Get available field types
 */
export function getComponentTypesSelectList(): SelectOption[] {
	return [...componentTypes];
}

/**
 * isAttributeValidForTag will compare the attribute against the properties
 * available for that tag to confirm whether it exists or not. This is to assist in stripping
 * outdated or invalid attributes in the customisation saving process.
 */
export function isAttributeValidForTag(
	tagName: string,
	attributeName: string,
): boolean {
	// Convert 'field-model.bind' to 'fieldModel'
	attributeName = attributeName.replace('.bind', '');
	attributeName = attributeName.replace(/-([a-z])/g, (g) =>
		g[1].toUpperCase(),
	);

	{
		const meta = propertiesMeta[tagName];
		if (meta !== undefined) {
			for (const prop of meta.props) {
				if (prop.name === attributeName) {
					return true;
				}
			}
		}
	}
	return false;
}

export function getRequirePathForTag(tagName: string): string {
	const typeMeta = propertiesMeta[tagName];
	if (typeMeta === undefined) {
		return '';
	}
	if (!typeMeta.requirePath) {
		throw new Error(
			tagName + ' has no require path defined. (ie. blank or undefined)',
		);
	}
	return typeMeta.requirePath;
}

export function getPropertiesForTag(tagName: string): FieldPropertyMeta[] {
	// NOTE: Jake: 2019-03-07
	// If we don't make a copy of each individual object then
	// if we jump between editing two different field types, it won't update
	// the UI.
	const properties = [];
	if (tagName !== '') {
		const typeMeta = propertiesMeta[tagName];
		if (typeMeta !== undefined) {
			for (const prop of typeMeta.props) {
				properties.push({ ...prop });
			}
		}
	}
	return properties;
}

export const baseFieldProperties: readonly FieldPropertyMeta[] = [
	{
		label: 'Field Type',
		name: 'originalTagName',
		type: 'FieldType',
	},
];

// Properties used by the Field class type
export const commonFieldProperties: readonly FieldPropertyMeta[] = [
	...baseFieldProperties,
	{
		label: 'Field Model',
		name: 'fieldModel',
		type: 'FieldModel',
	},
	{
		label: 'Custom Label',
		name: 'label',
		type: 'String',
	},
];

// NOTE: Jake: 2019-02-27
// Need to consider whether this should be a list that each field
// registers to from its own file to seperate concerns.
// For now this is the simplest implementation.
const componentTypes: SelectOption[] = [];

const propertiesMeta: { [fieldName: string]: FieldMeta } = {};
